import { QueryFunctionContext, useQueries, useQuery } from '@tanstack/react-query'
import { Identity, Maybe, Organization, ResourceId, ResourceTypes, SimpleMap } from '@vendorflow/types'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import {
  fetchAliceManagedByUsingGET,
  fetchBradBelongsToUsingGET,
  fetchIdentityUsingGET,
  fetchWatchedByIdentityUsingGET,
} from 'apis/Identity.api'
import { UseQueryOptions } from 'types/ReactQuery.type'
import { LONG_CACHE_TIME, LONG_STALE_TIME } from 'pages/WorkspacePage/Workspace.constants'
import { fetchExternalChannelUsingGET } from 'apis/Channel.api'
import { useMemo } from 'react'
import { fetchManagedIdentitiesInRootUsingGET } from 'apis/Identity.api'
import { convertResourceToEntity } from '@vendorflow/common/lib/converters/JsonApi.converter'
import ms from 'ms'

/**
 * Single Identity
 */
export function identityQueryKey(id: Maybe<string>): [QueryCacheEnum.IDENTITIES, Maybe<string>] {
  return [QueryCacheEnum.IDENTITIES, id]
}

export function identityQueryFn({ queryKey }: { queryKey: ReturnType<typeof identityQueryKey> }) {
  const id = queryKey[1]!
  return fetchIdentityUsingGET(id)
}

export function useIdentity(id: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true
  return useQuery<Identity, Error, Identity, ReturnType<typeof identityQueryKey>>(
    identityQueryKey(id),
    identityQueryFn,
    {
      enabled: Boolean(id && enabled),
      cacheTime: LONG_CACHE_TIME,
      staleTime: LONG_STALE_TIME,
    }
  )
}

/**
 * A map of identities for the provided ids
 */
export function useIdentities(ids: string[], options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: identityQueryKey(id),
      queryFn: identityQueryFn,
      cacheTime: LONG_CACHE_TIME,
      staleTime: LONG_STALE_TIME,
      enabled: Boolean(id && enabled),
    })),
  })
  const dataUpdatedAtHash = results.map((item) => item.dataUpdatedAt).join('')
  const dataUpdatedAt = useMemo(() => {
    const notAllLoaded = results.some((item) => !item.dataUpdatedAt)
    return notAllLoaded ? 0 : new Date().getTime()
  }, [dataUpdatedAtHash])
  const isInitialLoading = results.some((item) => item.isInitialLoading)
  const data = useMemo(() => {
    if (dataUpdatedAt) {
      return results.reduce<SimpleMap<Identity>>((acc, item) => {
        if (item.data) {
          acc[item.data.id] = item.data
        }

        return acc
      }, {})
    }

    return {}
  }, [dataUpdatedAt])

  return { data, dataUpdatedAt, isInitialLoading }
}

/**
 * The external Channel for an Alice
 */
export function channelForAliceQueryKey(alice: string) {
  return [QueryCacheEnum.IDENTITIES, alice, QueryCacheEnum.CHANNEL]
}

export function channelForAliceQueryFn(envy: string, alice: string) {
  return fetchExternalChannelUsingGET(envy, alice)
}

/**
 * Bob's watched resources
 */
export function watchedByBobQueryKey(bob: string) {
  return [QueryCacheEnum.IDENTITIES, bob, QueryCacheEnum.WATCHING]
}

export function watchedByBobQueryFn({ queryKey }: { queryKey: ReturnType<typeof watchedByBobQueryKey> }) {
  return fetchWatchedByIdentityUsingGET(queryKey[1])
}

export function useWatchedByBob(bob: Maybe<string>) {
  return useQuery<
    ResourceId<ResourceTypes>[],
    Error,
    ResourceId<ResourceTypes>[],
    ReturnType<typeof watchedByBobQueryKey>
  >(watchedByBobQueryKey(bob!), watchedByBobQueryFn, {
    enabled: Boolean(bob),
    cacheTime: ms('5m'),
    staleTime: ms('5m'),
  })
}

/**
 * Envies that manage Alice
 */
export function aliceManagedByKey(alice: string): [QueryCacheEnum.IDENTITIES, string, QueryCacheEnum.MANAGED_BY] {
  return [QueryCacheEnum.IDENTITIES, alice, QueryCacheEnum.MANAGED_BY]
}

export async function aliceManagedByFn({ queryKey }: QueryFunctionContext<ReturnType<typeof aliceManagedByKey>>) {
  const alice = queryKey[1]!
  return fetchAliceManagedByUsingGET(alice)
}

export function useAliceManagedByQuery(alice: Maybe<string>) {
  return useQuery<Organization[], Error, Organization[], ReturnType<typeof aliceManagedByKey>>({
    queryKey: aliceManagedByKey(alice!),
    queryFn: aliceManagedByFn,
    enabled: Boolean(alice),
  })
}

/**
 * Envies to which Brad belongs
 */
export function bradBelongsToQueryKey(brad: string): [QueryCacheEnum.IDENTITIES, string, QueryCacheEnum.BELONGS_TO] {
  return [QueryCacheEnum.IDENTITIES, brad, QueryCacheEnum.BELONGS_TO]
}

export async function bradBelongsToFn({ queryKey }: QueryFunctionContext<ReturnType<typeof bradBelongsToQueryKey>>) {
  const brad = queryKey[1]!
  return fetchBradBelongsToUsingGET(brad)
}

export function useBradBelongsToQuery(brad: Maybe<string>) {
  return useQuery<Organization[], Error, Organization[], ReturnType<typeof bradBelongsToQueryKey>>({
    queryKey: bradBelongsToQueryKey(brad!),
    queryFn: bradBelongsToFn,
    enabled: Boolean(brad),
  })
}

/**
 * All Alices In Root Org Query
 */
export function alicesForRootQueryKey(envyId: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envyId, QueryCacheEnum.ALICES]
}

export async function alicesForRootQueryFn({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof alicesForRootQueryKey>>): Promise<Identity[]> {
  const envy = queryKey[1]!
  const result = await fetchManagedIdentitiesInRootUsingGET(envy)
  return result.data.data.map((item) => convertResourceToEntity(item))
}

export function useAlicesForRoot(envyId: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<Identity[], Error, Identity[], ReturnType<typeof alicesForRootQueryKey>>(
    alicesForRootQueryKey(envyId),
    alicesForRootQueryFn,
    {
      enabled: !!envyId && enabled,
      staleTime: ms('5m'),
    }
  )
}
